import React, { useState, useMemo, useEffect } from "react";
import FilterIcon from "../../SVG/Filter";
import PDFViewer from "../Shared/PDFViewer";
import { useAuth } from "../../src/Context/AuthContext"

const AgentStatementsSection = ({ model, rowsPerPage = 20 }) => {
    const allDocs = model.statements || [];
    const downLines = model.downlines || [];

    // selected agent (defaults to model.agtCode)
    const [agtCode, setAgtCode] = useState(model.agtCode);
    const [filteredDocs, setFilteredDocs] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedDoc, setSelectedDoc] = useState(null);
    const [pdfBlob, setPdfBlob] = useState(null);
    const { logout } = useAuth();

    //ENV Variables
    const apiURL = import.meta.env.VITE_API;

    const totalEntries = filteredDocs.length;
    const totalPages = Math.ceil(totalEntries / rowsPerPage);

    // filter statements whenever agtCode changes
    useEffect(() => {
        const filtered = allDocs.filter(doc => doc.refNo === agtCode);
        setFilteredDocs(filtered);
        setCurrentPage(1);

        // Auto-select first document if available
        if (filtered.length > 0) {
            const first = filtered[0];
            setSelectedDoc(first);

            (async () => {
                const blob = await getPDF(first.id);
                if (blob) setPdfBlob(blob);
            })();
        } else {
            setSelectedDoc(null);
            setPdfBlob(null);
        }
    }, [agtCode, allDocs]);

    const paginatedDocs = useMemo(() => {
        const start = (currentPage - 1) * rowsPerPage;
        return filteredDocs.slice(start, start + rowsPerPage);
    }, [filteredDocs, currentPage, rowsPerPage]);

    const getPDF = async (id) => {
        try {
            const res = await fetch(`${apiURL}/image/pdf/download?documentID=${id}`, {
                method: "GET",
                credentials: "include"
            });

            if (res.status === 401) {
                logout();
                navigate('/login');
                throw new Error("unauthorized");
            }

            if (res.status === 403) {
                navigate('/home');
                throw new Error("forbidden");
            }

            if (!res.ok) {
                throw new Error("Failed to fetch PDF");
            }

            return await res.blob();
        } catch (err) {
            console.error(err);
            alert("Could not retrieve file");
            return null;
        }
    };

    const handleSelectDoc = async (doc) => {
        setSelectedDoc(doc);
        const blob = await getPDF(doc.id);
        if (blob) setPdfBlob(blob);
    };

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const from = totalEntries === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
    const to = Math.min(currentPage * rowsPerPage, totalEntries);

    return (
        <div className="container container-section" id="documents-container">
            {/* Filter Box - Always Visible */}
            <div className="filter-box">
                <FilterIcon />
                <div className="filter-item">
                    <label htmlFor="agtCode" className="filterLabel">Downline:</label>
                    <select
                        id="agtCode"
                        className="filterInput"
                        value={agtCode}
                        onChange={(e) => setAgtCode(e.target.value)}
                    >
                        <option value={model.agtCode}>
                            {model.agtCode} - {model.agentInfo.displayName || "You"}
                        </option>
                        {downLines.map((dl) => (
                            <option key={dl.agentCode} value={dl.agentCode}>
                                {dl.agentCode} - {dl.displayName}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {filteredDocs.length === 0 ? (
                <div className="card notfoundbox rounded-3 shadow-lg">
                    <div className="card-body">
                        <h5>No Statements Found</h5>
                    </div>
                </div>
            ) : (
                <div className="split-view">
                    {/* LEFT SIDE: Document List */}
                    <div className="doc-list">
                        <table className="policy-results-table">
                            <thead>
                                <tr>
                                    <th>Document Name</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedDocs.map((doc, index) => (
                                    <tr
                                        key={index}
                                        onClick={() => handleSelectDoc(doc)}
                                        className={selectedDoc?.id === doc.id ? "selected" : ""}
                                        style={{ cursor: "pointer" }}
                                    >
                                        <td>{doc.displayName}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {/* Pagination */}
                        <div className="pagination-bar">
                            <div className="pagination-summary">
                                Showing {from}-{to} of {totalEntries} entries
                            </div>
                            <div className="pagination-controls">
                                <button
                                    onClick={() => handlePageChange(1)}
                                    disabled={currentPage === 1}
                                >
                                    &#171;
                                </button>

                                <button
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                >
                                    &#8592;
                                </button>

                                {(() => {
                                    const visibleRange = 3;
                                    let start = Math.max(
                                        1,
                                        currentPage - Math.floor(visibleRange / 2)
                                    );
                                    let end = Math.min(
                                        totalPages,
                                        start + visibleRange - 1
                                    );

                                    if (end - start + 1 < visibleRange) {
                                        start = Math.max(
                                            1,
                                            end - visibleRange + 1
                                        );
                                    }

                                    const buttons = [];

                                    if (start > 1) {
                                        buttons.push(
                                            <span key="left-ellipsis">...</span>
                                        );
                                    }

                                    for (let page = start; page <= end; page++) {
                                        buttons.push(
                                            <button
                                                key={page}
                                                onClick={() => handlePageChange(page)}
                                                className={
                                                    currentPage === page
                                                        ? "active"
                                                        : ""
                                                }
                                            >
                                                {page}
                                            </button>
                                        );
                                    }

                                    if (end < totalPages) {
                                        buttons.push(
                                            <span key="right-ellipsis">...</span>
                                        );
                                    }

                                    return buttons;
                                })()}

                                <button
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                >
                                    &#8594;
                                </button>

                                <button
                                    onClick={() => handlePageChange(totalPages)}
                                    disabled={currentPage === totalPages}
                                >
                                    &#187;
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* RIGHT SIDE: PDF Viewer */}
                    <div className="pdf-viewer">
                        {pdfBlob ? (
                            <PDFViewer
                                src={URL.createObjectURL(pdfBlob)}
                                fileId={selectedDoc.id}
                                fileName={selectedDoc?.displayName}
                                serviceForm={false}
                            />
                        ) : (
                            <div className="no-selection">
                                <p>Select a document to view</p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default AgentStatementsSection;
